// The product catalog. Since #248 the live source is catalog-service via
// the BFF (`GET /api/products`, ADR 0051/0055). The bundled
// `shared/catalog/products.json` copy (`npm run package` / `predev`) is the
// fallback for local `vite dev` with no BFF and for first paint if the API
// is slow — it carries `sku`/`name`/`category`/`unitPrice`, everything the
// grid shows.

export interface Product {
  sku: string;
  name: string;
  category: string;
  unitPrice: number;
}

interface ApiProduct {
  sku: string;
  name: string;
  category: string;
  unitPrice: number;
}

interface ApiProductList {
  count: number;
  products: ApiProduct[];
}

/**
 * Live catalog from `GET /api/products`, falling back to the bundled copy.
 * Throws only if both fail — a storefront with no catalog has nothing to
 * show.
 */
export async function fetchCatalog(): Promise<Product[]> {
  try {
    const res = await fetch("/api/products", { credentials: "include" });
    if (res.ok) {
      const body = (await res.json()) as ApiProductList;
      if (Array.isArray(body.products)) {
        return body.products.map(({ sku, name, category, unitPrice }) => ({
          sku,
          name,
          category,
          unitPrice,
        }));
      }
    }
  } catch {
    // fall through to the bundled copy
  }

  const res = await fetch(`${import.meta.env.BASE_URL}products.json`, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`catalog unavailable (api + products.json ${res.status})`);
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error("products.json: expected an array");
  }
  return (data as Product[]).map(({ sku, name, category, unitPrice }) => ({
    sku,
    name,
    category,
    unitPrice,
  }));
}

/** Category display order — alphabetical happens to already read fine, but
 *  spelling it out here means a new category (a catalog change) shows up
 *  wherever `unknownCategoryOrder` puts it instead of silently being
 *  alphabetised into a spot no one chose. */
const CATEGORY_ORDER = ["Audio", "Fitness", "Home", "Kitchen", "Office", "Outdoor"];

export function groupByCategory(products: Product[]): [string, Product[]][] {
  const byCategory = new Map<string, Product[]>();
  for (const p of products) {
    const list = byCategory.get(p.category);
    if (list) {
      list.push(p);
    } else {
      byCategory.set(p.category, [p]);
    }
  }

  const known = CATEGORY_ORDER.filter((c) => byCategory.has(c));
  const unknown = [...byCategory.keys()].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
  return [...known, ...unknown].map((c) => [c, byCategory.get(c)!]);
}
